import { useRef } from 'react'
import css from 'classnames'
import { XYCoord, useDrag, useDrop } from 'react-dnd'
import { Link } from 'react-router-dom'
import { NestedCategories } from '~/shared/api'
import { moveElement, replaceCategoriesById } from '../../lib/helpers'
import { CategoryStatusIndicators } from './category-status-indicators'
import { SVGDragIndicator, SVGPencil } from '~/shared/ui'

export interface Props {
  data: NestedCategories
  subList: NestedCategories[]
  list: NestedCategories[]
  index: number
  parentItem: NestedCategories
  setList: (data: NestedCategories[]) => void
  updated: () => void
}

export const SubItem3 = ({
  data,
  index,
  parentItem,
  list,
  subList,
  setList,
  updated,
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const dragPreviewRef = useRef<HTMLDivElement | null>(null)

  const [, drop] = useDrop({
    accept: 'sub-category-3',

    hover: (item: NestedCategories & { index: number }, monitor) => {
      if (!dragPreviewRef.current) return

      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) return

      const hoveredRect = dragPreviewRef.current.getBoundingClientRect()
      const hoverMiddleY = (hoveredRect.bottom - hoveredRect.top) / 2
      const mousePosition = monitor.getClientOffset() as XYCoord
      const hoverClientY = mousePosition.y - hoveredRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      const newData = moveElement(subList, dragIndex, hoverIndex)
      const newList = replaceCategoriesById(list, parentItem.id, newData)

      setList(newList)
      updated()

      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'sub-category-3',
    item: { ...data, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(ref)
  drop(preview(dragPreviewRef))

  return (
    <div
      ref={dragPreviewRef}
      className={css(
        'category active:overflow-hidden bg-gray-100  w-full overflow-hidden rounded-lg border border-[#E0E0E0]',
        {
          'opacity-50': isDragging,
        },
      )}
      data-id={data.id}
      data-index={index}
    >
      <div className="flex h-[53px] min-w-0 items-center gap-3">
        <div
          ref={ref}
          className="flex cursor-grab item-center shrink-0 justify-center p-3 active:cursor-grabbing"
        >
          <SVGDragIndicator />
        </div>

        <div className="flex min-w-0 flex-1 items-center">
          <h2
            className={css('min-w-0 flex-1 truncate text-[18px]', {
              'opacity-50': !data.showInMenu,
            })}
          >
            {data.name}
          </h2>

          <div className="ml-3 flex shrink-0 items-center gap-3">
            <CategoryStatusIndicators
              enabled={data.enabled}
              showInMenu={data.showInMenu}
            />

            <Link
              to={`/categories/${data.id}/edit`}
              className="group flex shrink-0 items-center justify-center"
            >
              <div
                className={css('text-gray-400', {
                  'opacity-50': !data.showInMenu,
                })}
              >
                <SVGPencil />
              </div>
            </Link>
          </div>
        </div>

        <div className="shrink-0 pr-3">
          {data?.categories?.length ? (
            <span className="flex h-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[12px] text-white">
              {data.categories.length}
            </span>
          ) : (
            <span />
          )}
        </div>
      </div>
    </div>
  )
}
